/**
 * PHARMAKON — BETA mock router
 *
 * Maps (METHOD, urlPattern) → handler. Handlers receive a `ctx` object:
 *
 *   ctx.method   : 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
 *   ctx.url      : full request path (after the API base is stripped)
 *   ctx.params   : { ...querystring, ...routeParams }
 *   ctx.body     : parsed JSON body (or undefined)
 *   ctx.store    : the live in-memory store (handlers mutate freely)
 *   ctx.commit() : persist mutations to localStorage
 *
 * A handler returns either:
 *   - any plain value      → normalised to the real backend body shape
 *                            ({ data: value }) and wrapped { status: 200, data }
 *   - { status, data }     → used verbatim (errors, blobs, custom status)
 *
 * Patterns use `:name` for path parameters (Express-style).
 */
import { ensureStore, commit, store } from "./store.js";
import { registerAll } from "./handlers/index.js";

const routes = [];

/**
 * Compile a pattern into a matcher. Supports:
 *   - ":name"  → a single path segment (captured)
 *   - "*"      → a greedy wildcard across segments (uncaptured) — used by the
 *                graceful catch-all stubs for admin/comms areas that the
 *                pharmacy beta never reaches but must not 404 on.
 */
function compile(pattern) {
  const keys = [];
  const body = pattern
    .replace(/:([A-Za-z_]+)/g, (_, k) => {
      keys.push(k);
      return "([^/]+)";
    })
    .replace(/\*/g, ".*");
  const re = new RegExp("^" + body + "/?$");
  return { re, keys };
}

/** Register a single route. Public API used by ./handlers/*. */
export function route(method, pattern, handler) {
  const { re, keys } = compile(pattern);
  routes.push({ method: method.toUpperCase(), re, keys, handler, pattern });
}

let registered = false;
function lazyRegister() {
  if (registered) return;
  registerAll(route);
  registered = true;
}

/** Internal — match a method+url to a route or return null. */
function findRoute(method, url) {
  const cleanUrl = url.split("?")[0];
  for (const r of routes) {
    if (r.method !== method && r.method !== "*") continue;
    const m = cleanUrl.match(r.re);
    if (m) {
      const params = {};
      r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1])));
      return { route: r, params };
    }
  }
  return null;
}

/**
 * Dispatch a request to the matching handler. Returns { status, data } or
 * a structured 404/500 error object the axios adapter wraps.
 */
export async function dispatch({ method, url, body, params }) {
  lazyRegister();
  ensureStore();
  const upMethod = method.toUpperCase();

  const m = findRoute(upMethod, url);
  if (!m) {
    return {
      status: 404,
      data: {
        success: false,
        message: `[BETA] Endpoint non implémenté en mode démo : ${upMethod} ${url}`,
        code: "DEMO_NOT_IMPLEMENTED",
      },
    };
  }

  try {
    const ctx = {
      method: upMethod,
      url,
      body,
      params: { ...(params || {}), ...m.params },
      store,
      commit,
    };
    const result = await m.route.handler(ctx);

    // Explicit { status, data } response (errors, blobs, custom HTTP status) —
    // returned verbatim so handlers can set non-200 codes.
    if (
      result &&
      typeof result === "object" &&
      "status" in result &&
      "data" in result
    ) {
      return result;
    }

    // Otherwise normalise to the REAL backend's HTTP-body convention that the
    // stores/pages are written against: the body is ALWAYS shaped
    //   { data: <payload>, ...meta }
    // The axios response interceptor unwraps to `res.data`, so callers read
    // `res.data` (single resource / dashboard) or `res.data` + `res.total`
    // (lists). Paginated handlers already return { data:[...], total, ... } and
    // pass through untouched; single-resource & action handlers return their
    // payload raw and get wrapped here so `res.data` resolves correctly.
    return { status: 200, data: toEnvelope(result) };
  } catch (e) {
    // Mock handler errors propagate to the axios adapter, which converts them
    // into AxiosError-shaped rejections. Status defaults to 500.
    const status = e?.status ?? 500;
    return {
      status,
      data: {
        success: false,
        message: e?.message ?? "Erreur interne (mode démo)",
        code: e?.code ?? "DEMO_HANDLER_ERROR",
      },
    };
  }
}

/** Throw helper for handlers — translates to a structured response. */
export function httpError(status, message, code) {
  const e = new Error(message);
  e.status = status;
  e.code = code;
  throw e;
}

/**
 * Normalise a handler's raw return into the real backend's HTTP body shape.
 *
 *   - Already an envelope (a plain object that owns a `data` key — every
 *     paginated list, since paginate() returns { data, total, page, limit }):
 *     passed through, so we never double-wrap a list into
 *     { data: { data: [...] } }.
 *   - Single resource / action result / array payload: wrapped as
 *     { data: payload }. `success` / `message` / `total` are also hoisted to
 *     the top level so callers that read those siblings directly (e.g.
 *     `res.success`) keep working alongside callers that read `res.data`.
 *   - null / primitive: wrapped as { data: payload } too (e.g. /caisse/current
 *     returning null → { data: null }, which callers read as `res.data`).
 */
function toEnvelope(payload) {
  const isPlainObject =
    payload !== null && typeof payload === "object" && !Array.isArray(payload);
  if (isPlainObject && "data" in payload) return payload; // already an envelope
  const env = { data: payload };
  if (isPlainObject) {
    if ("success" in payload) env.success = payload.success;
    if ("message" in payload) env.message = payload.message;
    if ("total" in payload) env.total = payload.total;
  }
  return env;
}
